/**
 * People Ops Pages — Benefits (§26), Promotions & Salary (§27), Transfers (§28), Overtime (§18).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, HeartHandshake, TrendingUp, ArrowRightLeft, Clock, CheckCircle2, XCircle, Users } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const Card = ({ children, className = '' }) => (<div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>);
const H1 = ({ eyebrow, title, sub }) => (
  <div className="mb-6">
    <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">{eyebrow}</span>
    <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
    {sub && <p className="mt-1 text-sm text-[#0b1a2e]/60">{sub}</p>}
  </div>
);
const Btn = ({ variant = 'primary', className = '', ...rest }) => {
  const styles = { primary: 'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]', ghost: 'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]', reject: 'border border-red-200 text-red-600' }[variant];
  return <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles} ${className}`} />;
};
const Input = (p) => <input {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${p.className || ''}`} />;
const Select = (p) => <select {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${p.className || ''}`}>{p.children}</select>;
const Field = ({ label, children }) => (<div><label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>{children}</div>);
const Badge = ({ tone = 'green', children }) => <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${{green:'bg-emerald-100 text-emerald-800',amber:'bg-amber-100 text-amber-800',red:'bg-red-100 text-red-800',slate:'bg-slate-100 text-slate-700',sky:'bg-sky-100 text-sky-800'}[tone]}`}>{children}</span>;

// ============================================================
// BENEFITS
// ============================================================
export const BenefitsPage = () => {
  const [tab, setTab] = useState('plans');
  return (
    <div>
      <H1 eyebrow="Global HR · §26" title="Benefits" sub="Medical, insurance, pension, transport, mobile, meal — with employee/company/both cost split." />
      <div className="flex gap-2 mb-4">
        {[['plans','Plans'],['enrolments','Enrolments']].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} data-testid={`ben-tab-${k}`} className={`px-3 py-2 rounded-full text-xs font-semibold ${tab===k?'bg-[#0f5c3d] text-white':'border border-[#0f5c3d]/20 text-[#0f5c3d]'}`}>{l}</button>
        ))}
      </div>
      {tab==='plans'?<BenefitPlansTab/>:<BenefitEnrolmentsTab/>}
    </div>
  );
};

const BenefitPlansTab = () => {
  const [items, setItems] = useState([]); const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); const [enrolling, setEnrolling] = useState(null);
  const load = useCallback(async () => {
    const [p, u] = await Promise.all([apiClient.get('/hr/benefits/plans'), apiClient.get('/users/directory')]);
    setItems(p.data.data || []); setUsers(u.data.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (p) => { if (window.confirm(`Delete ${p.name}?`)) { await apiClient.delete(`/hr/benefits/plans/${p.id}`); load(); } };
  return (
    <>
      <div className="flex justify-end mb-3"><Btn onClick={()=>setEditing({})} data-testid="new-benefit-btn"><Plus className="w-3.5 h-3.5"/> New plan</Btn></div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">Kind</th><th className="text-left p-2">Provider</th><th className="text-right p-2">Monthly</th><th className="text-left p-2">Paid by</th><th></th></tr></thead>
          <tbody>{items.map((p)=>(
            <tr key={p.id} className="border-t border-black/5" data-testid={`benefit-row-${p.code}`}>
              <td className="p-2 font-mono text-xs">{p.code}</td><td className="p-2"><HeartHandshake className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1.5"/>{p.name}</td><td className="p-2 text-xs">{p.kind}</td><td className="p-2 text-xs">{p.provider||'—'}</td><td className="p-2 text-right">{p.cost_monthly||0}</td><td className="p-2"><Badge tone={p.paid_by==='company'?'green':p.paid_by==='employee'?'slate':'amber'}>{p.paid_by}</Badge></td>
              <td className="p-2 text-right whitespace-nowrap"><Btn variant="ghost" onClick={()=>setEnrolling(p)} data-testid={`enrol-benefit-${p.code}`}><Users className="w-3.5 h-3.5"/> Enrol</Btn><button onClick={()=>setEditing(p)} className="text-[#0f5c3d] ml-2"><Pencil className="w-3.5 h-3.5 inline"/></button><button onClick={()=>del(p)} className="text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5 inline"/></button></td>
            </tr>
          ))}</tbody>
        </table>
        {items.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No plans yet.</div>}
      </Card>
      {editing && <BenefitPlanModal initial={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null);load();}} />}
      {enrolling && <EnrolBenefitModal plan={enrolling} users={users} onClose={()=>setEnrolling(null)} onSaved={()=>setEnrolling(null)} />}
    </>
  );
};

const BenefitPlanModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ code:'', name:'', kind:'medical', country:'', provider:'', cost_monthly:0, paid_by:'company', employer_share_pct:100, description:'', active:true, ...initial });
  const save = async () => {
    try { const p = {...f, cost_monthly: parseFloat(f.cost_monthly||0), employer_share_pct: parseFloat(f.employer_share_pct||0), code:(f.code||'').toUpperCase()};
      if (f.id) await apiClient.patch(`/hr/benefits/plans/${f.id}`, p); else await apiClient.post('/hr/benefits/plans', p);
      toast({title:'Saved'}); onSaved();
    } catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id?'Edit':'New'} benefit plan</div><button onClick={onClose}><X className="w-5 h-5"/></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Code"><Input value={f.code} onChange={(e)=>setF({...f,code:e.target.value})} data-testid="bp-code"/></Field>
          <Field label="Kind"><Select value={f.kind} onChange={(e)=>setF({...f,kind:e.target.value})}>{['medical','insurance','life','pension','accommodation','transport','mobile','vehicle','meal','other'].map(k=><option key={k} value={k}>{k}</option>)}</Select></Field>
          <div className="col-span-2"><Field label="Name"><Input value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} data-testid="bp-name"/></Field></div>
          <Field label="Country"><Input value={f.country||''} onChange={(e)=>setF({...f,country:e.target.value.toUpperCase()})}/></Field>
          <Field label="Provider"><Input value={f.provider||''} onChange={(e)=>setF({...f,provider:e.target.value})}/></Field>
          <Field label="Cost/month"><Input type="number" value={f.cost_monthly} onChange={(e)=>setF({...f,cost_monthly:e.target.value})}/></Field>
          <Field label="Paid by"><Select value={f.paid_by} onChange={(e)=>setF({...f,paid_by:e.target.value})}>{['employee','company','both'].map(k=><option key={k} value={k}>{k}</option>)}</Select></Field>
          <Field label="Employer share %"><Input type="number" value={f.employer_share_pct} onChange={(e)=>setF({...f,employer_share_pct:e.target.value})}/></Field>
          <div className="col-span-2"><Field label="Description"><Input value={f.description||''} onChange={(e)=>setF({...f,description:e.target.value})}/></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="bp-save">Save</Btn></div>
        </div>
      </div>
    </div>
  );
};

const EnrolBenefitModal = ({ plan, users, onClose, onSaved }) => {
  const [sel, setSel] = useState({}); const [eff, setEff] = useState(new Date().toISOString().slice(0,10));
  const submit = async () => {
    const ids = Object.keys(sel).filter((k)=>sel[k]); if (!ids.length) return toast({title:'Select at least one',variant:'destructive'});
    try { const r = await apiClient.post('/hr/benefits/enrol', {plan_id:plan.id, user_ids:ids, effective_from:eff}); toast({title:`${r.data.inserted} enrolled`}); onSaved(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">Enrol into {plan.name}</div><button onClick={onClose}><X className="w-5 h-5"/></button></div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <Field label="Effective from"><Input type="date" value={eff} onChange={(e)=>setEff(e.target.value)}/></Field>
          <div className="border border-black/5 rounded max-h-[40vh] overflow-y-auto">{users.map((u)=>(<label key={u.user_id} className="flex items-center gap-2 p-2 border-b border-black/5 hover:bg-[#f7fbf8]"><input type="checkbox" checked={!!sel[u.user_id]} onChange={(e)=>setSel({...sel,[u.user_id]:e.target.checked})}/><span className="text-sm">{u.name||u.email}</span><span className="text-xs text-[#0b1a2e]/50 ml-auto">{u.role}</span></label>))}</div>
        </div>
        <div className="p-4 border-t bg-[#f7fbf8] flex justify-end gap-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={submit} data-testid="benefit-enrol-submit">Enrol</Btn></div>
      </div>
    </div>
  );
};

const BenefitEnrolmentsTab = () => {
  const [items, setItems] = useState([]); const [users, setUsers] = useState([]);
  useEffect(() => { Promise.all([apiClient.get('/hr/benefits/enrolments'), apiClient.get('/users/directory')]).then(([a,b])=>{setItems(a.data.data||[]);setUsers(b.data.data||[]);}); }, []);
  const end = async (e) => { if (window.confirm('End this enrolment?')) { await apiClient.post(`/hr/benefits/enrolments/${e.id}/end`); setItems(items.filter((x)=>x.id!==e.id)); } };
  return (
    <Card>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Plan</th><th className="text-left p-2">Employee</th><th className="text-left p-2">Kind</th><th className="text-left p-2">Effective</th><th></th></tr></thead>
        <tbody>{items.map((e)=>{const u=users.find((uu)=>uu.user_id===e.user_id);return(<tr key={e.id} className="border-t border-black/5"><td className="p-2">{e.plan_name}</td><td className="p-2">{u?.name||e.user_id}</td><td className="p-2 text-xs">{e.plan_kind}</td><td className="p-2 text-xs">{e.effective_from}</td><td className="p-2 text-right"><Btn variant="ghost" onClick={()=>end(e)}>End</Btn></td></tr>);})}</tbody>
      </table>
      {items.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No active enrolments.</div>}
    </Card>
  );
};

// ============================================================
// PROMOTIONS
// ============================================================
export const PromotionsPage = () => {
  const [items, setItems] = useState([]); const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const [a,b] = await Promise.all([apiClient.get('/hr/promotions'), apiClient.get('/users/directory')]); setItems(a.data.data||[]); setUsers(b.data.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (p, stage, decision) => {
    const note = window.prompt(`Note (${decision}):`, '');
    try { await apiClient.post(`/hr/promotions/${p.id}/decide`, {stage, decision, note}); toast({title:`${decision}`}); load(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · §27" title="Promotions & salary increase" sub="Manager → HR → Finance → Management. Old & new salary preserved in history." />
        <div className="flex gap-2 mb-6"><Btn onClick={()=>setShowNew(true)} data-testid="new-promotion-btn"><Plus className="w-3.5 h-3.5"/> New promotion</Btn></div>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Employee</th><th className="text-left p-2">Change</th><th className="text-left p-2">Effective</th><th className="text-left p-2">Stage</th><th className="text-left p-2">Status</th><th></th></tr></thead>
          <tbody>{items.map((p)=>(
            <tr key={p.id} className="border-t border-black/5" data-testid={`promo-row-${p.id}`}>
              <td className="p-2 font-medium">{p.user_name}</td>
              <td className="p-2 text-xs"><TrendingUp className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1"/>{p.old_designation||'—'} → <b>{p.new_designation||'—'}</b> · {p.old_salary||'—'} → <b>{p.new_salary||'—'}</b> {p.currency||''}</td>
              <td className="p-2 text-xs">{p.effective_from}</td>
              <td className="p-2 text-xs">{p.stage}</td>
              <td className="p-2"><Badge tone={p.status==='approved'?'green':p.status==='rejected'?'red':'amber'}>{p.status}</Badge></td>
              <td className="p-2 text-right whitespace-nowrap">
                {p.status!=='approved' && p.status!=='rejected' && p.stage !== 'applied' && (<>
                  <Btn variant="ghost" onClick={()=>decide(p,p.stage,'approved')} data-testid={`promo-approve-${p.id}`}><CheckCircle2 className="w-3.5 h-3.5"/> {p.stage}</Btn>
                  <Btn variant="reject" onClick={()=>decide(p,p.stage,'rejected')} className="ml-1"><XCircle className="w-3.5 h-3.5"/></Btn>
                </>)}
              </td>
            </tr>
          ))}</tbody>
        </table>
        {items.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No promotion requests.</div>}
      </Card>
      {showNew && <PromotionModal users={users} onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false);load();}} />}
    </div>
  );
};

const PromotionModal = ({ users, onClose, onSaved }) => {
  const [f, setF] = useState({ user_id:'', new_designation:'', new_grade:'', new_salary:'', currency:'USD', effective_from:new Date().toISOString().slice(0,10), reason:'', notes:'' });
  const save = async () => {
    try { await apiClient.post('/hr/promotions', {...f, new_salary: f.new_salary?parseFloat(f.new_salary):null}); toast({title:'Submitted'}); onSaved(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New promotion</div><button onClick={onClose}><X className="w-5 h-5"/></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Employee"><Select value={f.user_id} onChange={(e)=>setF({...f,user_id:e.target.value})} data-testid="promo-user"><option value="">Select</option>{users.map((u)=><option key={u.user_id} value={u.user_id}>{u.name||u.email}</option>)}</Select></Field></div>
          <Field label="New designation"><Input value={f.new_designation} onChange={(e)=>setF({...f,new_designation:e.target.value})} data-testid="promo-designation"/></Field>
          <Field label="New grade"><Input value={f.new_grade} onChange={(e)=>setF({...f,new_grade:e.target.value})}/></Field>
          <Field label="New salary"><Input type="number" value={f.new_salary} onChange={(e)=>setF({...f,new_salary:e.target.value})}/></Field>
          <Field label="Currency"><Input value={f.currency} onChange={(e)=>setF({...f,currency:e.target.value.toUpperCase()})}/></Field>
          <Field label="Effective from"><Input type="date" value={f.effective_from} onChange={(e)=>setF({...f,effective_from:e.target.value})}/></Field>
          <div className="col-span-2"><Field label="Reason"><Input value={f.reason} onChange={(e)=>setF({...f,reason:e.target.value})}/></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="promo-save">Submit</Btn></div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TRANSFERS
// ============================================================
export const TransfersPage = () => {
  const [items, setItems] = useState([]); const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const [a,b] = await Promise.all([apiClient.get('/hr/transfers'), apiClient.get('/users/directory')]); setItems(a.data.data||[]); setUsers(b.data.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · §28" title="Transfers" sub="Department / position / manager / location / country / grade change. Previous state preserved in history." />
        <div className="flex gap-2 mb-6"><Btn onClick={()=>setShowNew(true)} data-testid="new-transfer-btn"><Plus className="w-3.5 h-3.5"/> New transfer</Btn></div>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Employee</th><th className="text-left p-2">Kind</th><th className="text-left p-2">Change</th><th className="text-left p-2">Effective</th><th className="text-left p-2">Reason</th></tr></thead>
          <tbody>{items.map((t)=>(
            <tr key={t.id} className="border-t border-black/5" data-testid={`transfer-row-${t.id}`}>
              <td className="p-2 font-medium">{t.user_name}</td>
              <td className="p-2"><Badge tone="sky">{t.kind}</Badge></td>
              <td className="p-2 text-xs"><ArrowRightLeft className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1"/>{t.old_value||'—'} → <b>{t.new_value}</b></td>
              <td className="p-2 text-xs">{t.effective_from}</td>
              <td className="p-2 text-xs text-[#0b1a2e]/60">{t.reason||'—'}</td>
            </tr>
          ))}</tbody>
        </table>
        {items.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No transfers yet.</div>}
      </Card>
      {showNew && <TransferModal users={users} onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false);load();}} />}
    </div>
  );
};

const TransferModal = ({ users, onClose, onSaved }) => {
  const [f, setF] = useState({ user_id:'', kind:'department', new_value:'', effective_from:new Date().toISOString().slice(0,10), reason:'' });
  const save = async () => {
    try { await apiClient.post('/hr/transfers', f); toast({title:'Applied'}); onSaved(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">New transfer</div>
        <div className="space-y-3">
          <Field label="Employee"><Select value={f.user_id} onChange={(e)=>setF({...f,user_id:e.target.value})} data-testid="tr-user"><option value="">Select</option>{users.map((u)=><option key={u.user_id} value={u.user_id}>{u.name||u.email}</option>)}</Select></Field>
          <Field label="Kind"><Select value={f.kind} onChange={(e)=>setF({...f,kind:e.target.value})}>{['department','position','manager','location','country','grade'].map(k=><option key={k} value={k}>{k}</option>)}</Select></Field>
          <Field label="New value"><Input value={f.new_value} onChange={(e)=>setF({...f,new_value:e.target.value})} data-testid="tr-value"/></Field>
          <Field label="Effective from"><Input type="date" value={f.effective_from} onChange={(e)=>setF({...f,effective_from:e.target.value})}/></Field>
          <Field label="Reason"><Input value={f.reason} onChange={(e)=>setF({...f,reason:e.target.value})}/></Field>
          <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="tr-save">Apply</Btn></div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// OVERTIME
// ============================================================
export const OvertimePage = () => {
  const [items, setItems] = useState([]); const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const r = await apiClient.get('/hr/overtime'); setItems(r.data.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (o, decision) => {
    const hrs = decision==='approved' ? window.prompt(`Approved hours (default ${o.hours}):`, o.hours) : null;
    const rate = decision==='approved' ? window.prompt('Hourly rate:', '') : null;
    try { await apiClient.post(`/hr/overtime/${o.id}/decide`, {decision, approved_hours: hrs?parseFloat(hrs):o.hours, rate: rate?parseFloat(rate):0}); toast({title:decision}); load(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · §18" title="Overtime" sub="Submit → manager approve → payroll. Country-specific rates applied at approval." />
        <div className="flex gap-2 mb-6"><Btn onClick={()=>setShowNew(true)} data-testid="new-ot-btn"><Plus className="w-3.5 h-3.5"/> Log overtime</Btn></div>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Employee</th><th className="text-left p-2">Date</th><th className="text-left p-2">Time</th><th className="text-right p-2">Hrs</th><th className="text-right p-2">Approved / Amount</th><th className="text-left p-2">Status</th><th></th></tr></thead>
          <tbody>{items.map((o)=>(
            <tr key={o.id} className="border-t border-black/5" data-testid={`ot-row-${o.id}`}>
              <td className="p-2 font-medium">{o.user_name}</td>
              <td className="p-2 text-xs">{o.date}</td>
              <td className="p-2 text-xs">{o.start_time} → {o.end_time}</td>
              <td className="p-2 text-right">{o.hours}</td>
              <td className="p-2 text-right text-xs">{o.approved_hours||'—'} / {o.ot_amount||'—'}</td>
              <td className="p-2"><Badge tone={o.status==='approved'?'green':o.status==='rejected'?'red':'amber'}>{o.status}</Badge></td>
              <td className="p-2 text-right whitespace-nowrap">
                {o.status==='pending' && (<>
                  <Btn variant="ghost" onClick={()=>decide(o,'approved')} data-testid={`ot-approve-${o.id}`}><CheckCircle2 className="w-3.5 h-3.5"/></Btn>
                  <Btn variant="reject" onClick={()=>decide(o,'rejected')} className="ml-1"><XCircle className="w-3.5 h-3.5"/></Btn>
                </>)}
              </td>
            </tr>
          ))}</tbody>
        </table>
        {items.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No overtime yet.</div>}
      </Card>
      {showNew && <OvertimeModal onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false);load();}} />}
    </div>
  );
};

const OvertimeModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ date:new Date().toISOString().slice(0,10), start_time:'18:00', end_time:'21:00', hours:3, reason:'' });
  const save = async () => {
    try { await apiClient.post('/hr/overtime', {...f, hours:parseFloat(f.hours)}); toast({title:'Submitted'}); onSaved(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3"><Clock className="w-4 h-4 inline text-[#0f5c3d] mr-1"/> Log overtime</div>
        <div className="space-y-3">
          <Field label="Date"><Input type="date" value={f.date} onChange={(e)=>setF({...f,date:e.target.value})}/></Field>
          <div className="grid grid-cols-3 gap-2"><Field label="Start"><Input type="time" value={f.start_time} onChange={(e)=>setF({...f,start_time:e.target.value})}/></Field><Field label="End"><Input type="time" value={f.end_time} onChange={(e)=>setF({...f,end_time:e.target.value})}/></Field><Field label="Hours"><Input type="number" step="0.5" value={f.hours} onChange={(e)=>setF({...f,hours:e.target.value})}/></Field></div>
          <Field label="Reason"><Input value={f.reason} onChange={(e)=>setF({...f,reason:e.target.value})}/></Field>
          <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="ot-save">Submit</Btn></div>
        </div>
      </div>
    </div>
  );
};
